import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { isToday } from 'date-fns'

import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { groupByKey } from '@fortem-monorepo/js-utilities'
import { useWindowTabs } from '@fortem-monorepo/react-hooks'
import { useApi } from '@fortem-monorepo/react-spa'

import { scheduledTasksOptions } from '@/react-query/queries/logs'

type useMonitoringContextHelpers = {
  addWindowTab: ReturnType<typeof useWindowTabs>['addWindowTab']
}

export const useMonitoringContextCreator = (helpers: useMonitoringContextHelpers) => {
  const api = useApi()
  const [selectedDate, setSelectedDate] = useState(getT())

  const scheduledTasksQuery = useQuery({
    ...scheduledTasksOptions(api),
    refetchInterval: (data) => (data.state.error ? 0 : 2000),
    enabled: isToday(selectedDate),
  })

  const scheduledTasks = useMemo(() => {
    return scheduledTasksQuery.data ?? []
  }, [scheduledTasksQuery.data])

  const groupedByDate = useMemo(
    () => groupByKey(scheduledTasks, 'date'),
    [scheduledTasks, selectedDate],
  )

  const visibleTasks = useMemo(() => {
    return groupedByDate[toISO8601(selectedDate)] ?? []
  }, [groupedByDate])

  const completedTasks = useMemo(() => {
    return visibleTasks.filter((task) => task.status === 2)
  }, [groupedByDate, selectedDate])

  const failedTasks = useMemo(() => {
    return visibleTasks.filter((task) => task.status === 1)
  }, [groupedByDate, selectedDate])

  const hasDataForDate = useCallback(
    (date: Date) => {
      return Object.keys(groupedByDate).includes(toISO8601(date))
    },
    [visibleTasks],
  )

  return {
    selectedDate,
    setSelectedDate,
    scheduledTasksQuery,
    scheduledTasks,
    groupedByDate,
    visibleTasks,
    completedTasks,
    failedTasks,
    hasDataForDate,
    ...helpers,
  }
}

type MonitoringContext = ReturnType<typeof useMonitoringContextCreator>

export const monitoringContext = createContext<MonitoringContext | null>(null)

export const useMonitoringContext = () => {
  const context = useContext(monitoringContext)
  if (!context) {
    throw new Error('Monitoring Context must be used inside a MonitoringContextProvider.')
  }
  return context
}
